import React from "react";
import { useLocation } from "react-router-dom";
import { useImmer } from "use-immer";
import moment from "moment";
import SimpleReactValidator from "simple-react-validator";
import { useDropdownApis } from "../../../shared";
import {
  arraySort,
  errorMessage,
  saveFileHelper,
  successMessage,
} from "../../../utils";
import { exportSalesVatReportApi } from "../api";

const getLastMonthStart = () =>
  moment().subtract(1, "month").startOf("month").toDate();
const getLastMonthEnd = () =>
  moment().subtract(1, "month").endOf("month").toDate();

// Error bodies of a blob request are blobs too, so read the validation message from the text
const getValidationMessage = async (error) => {
  const data = error?.response?.data;
  if (error?.response?.status !== 422 || !(data instanceof Blob)) return null;
  try {
    const body = JSON.parse(await data.text());
    const firstError = body.errors && Object.values(body.errors)[0]?.[0];
    return firstError || body.message || null;
  } catch (parseError) {
    return null;
  }
};

const MONTH_RULES = "required|past_month";
const STORE_RULES = "required";

const getFileName = (response, fallback) => {
  const header = response.headers?.["content-disposition"] ?? "";
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? match[1] : fallback;
};

export const useSalesVatReport = () => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");

  // Store admins always report on their own store, so the store list is super admin only
  const { storeList } = useDropdownApis({ isStoreList: !isStoreAdmin });
  const storeOptions = React.useMemo(
    () =>
      arraySort(
        storeList.map(({ id, name }) => ({ value: id, label: name })),
        "label",
      ),
    [storeList],
  );

  // Only fully ended months can be reported
  const maxMonth = React.useMemo(() => getLastMonthEnd(), []);

  const [state, setState] = useImmer({
    isExporting: false,
    filter: { month: getLastMonthStart(), store: null },
  });
  const [, setForceUpdate] = React.useState(0);

  // `element: false` keeps messages as plain strings so they fit MUI helper text
  const validator = React.useRef(
    new SimpleReactValidator({
      element: false,
      autoForceUpdate: { forceUpdate: setForceUpdate },
      validators: {
        past_month: {
          message: "The month must be a past month.",
          rule: (value) =>
            moment(value).isValid() &&
            !moment(value).isAfter(getLastMonthEnd()),
        },
      },
    }),
  );

  const handleMonthChange = (value) => {
    // InputControl also forwards the text input's change event to this handler; only dates are valid
    if (value && !(value instanceof Date)) return;
    setState((draft) => {
      draft.filter.month = value;
    });
  };

  const handleStoreChange = (_, value) => {
    setState((draft) => {
      draft.filter.store = value;
    });
  };

  const { month, store } = state.filter;
  const errors = {
    month: validator.current.message("month", month, MONTH_RULES),
    // The store is only registered (and so only required) for super admin
    store: isStoreAdmin
      ? undefined
      : validator.current.message("store", store, STORE_RULES),
  };

  const handleGenerate = async () => {
    if (state.isExporting) return;
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      return;
    }
    const reportMonth = moment(month).format("YYYY-MM");
    const storeId = isStoreAdmin ? undefined : store.value;

    setState((draft) => {
      draft.isExporting = true;
    });
    try {
      const response = await exportSalesVatReportApi({
        month: reportMonth,
        store_id: storeId,
      });
      await saveFileHelper({
        response,
        fileName: getFileName(
          response,
          `sales-vat-report-${storeId ? `${storeId}-` : ""}${reportMonth}.pdf`,
        ),
      });
      successMessage("Sales VAT report generated");
    } catch (error) {
      errorMessage(
        (await getValidationMessage(error)) ??
          "Failed to generate the Sales VAT report",
      );
    } finally {
      setState((draft) => {
        draft.isExporting = false;
      });
    }
  };

  return {
    state,
    errors,
    handlers: {
      handleMonthChange,
      handleStoreChange,
      handleGenerate,
    },
    isStoreAdmin,
    storeOptions,
    maxMonth,
  };
};
